import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SanseiEvent } from 'r/types';
import { captureException, showNotification } from 'r/utils';
import {
  fetchApiRaw,
  DELETE_EVENT_PLAY,
  PUT_EVENT_PLAY,
} from 'r/utils/fetchApiRaw';
import { StoredAuth } from 'r/utils/Stored';

export function useDeleteEvents() {
  const queryClient = useQueryClient();
  const accessTokens = StoredAuth.getAll();

  return useMutation({
    mutationFn: async (eventIds: string[]) => {
      await Promise.all(
        eventIds.map((id) =>
          fetchApiRaw(DELETE_EVENT_PLAY, { urlParams: [id] }, accessTokens.cms),
        ),
      );
    },
    onSuccess: (_data, eventIds) => {
      showNotification(
        'success',
        `Deleted ${eventIds.length} event${eventIds.length > 1 ? 's' : ''}`,
      );
      queryClient.invalidateQueries({ queryKey: ['cms-events-play'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to delete event');
    },
  });
}

export function useRestoreEvent() {
  const queryClient = useQueryClient();
  const accessTokens = StoredAuth.getAll();

  return useMutation({
    mutationFn: async (event: SanseiEvent) => {
      await fetchApiRaw(
        PUT_EVENT_PLAY,
        {
          urlParams: [event.id],
          body: { deletedAt: null },
        },
        accessTokens.cms,
      );
      return event;
    },
    onSuccess: () => {
      showNotification('success', 'Event restored successfully');
      queryClient.invalidateQueries({ queryKey: ['cms-events-play'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to restore event');
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  const accessTokens = StoredAuth.getAll();

  return useMutation({
    mutationFn: async ({
      eventId,
      body,
    }: {
      eventId: string;
      body: Record<string, unknown>;
    }) => {
      await fetchApiRaw(
        PUT_EVENT_PLAY,
        {
          urlParams: [eventId],
          body,
        },
        accessTokens.cms,
      );
    },
    onSuccess: () => {
      showNotification('success', 'Event updated successfully');
      queryClient.invalidateQueries({ queryKey: ['cms-events-play'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to update event');
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  const accessTokens = StoredAuth.getAll();

  return useMutation({
    mutationFn: async (eventId: string) => {
      await fetchApiRaw(
        DELETE_EVENT_PLAY,
        { urlParams: [eventId] },
        accessTokens.cms,
      );
    },
    onSuccess: () => {
      showNotification('success', 'Event deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['cms-events-play'] });
    },
    onError: (error) => {
      captureException(error);
      showNotification('error', 'Failed to delete event');
    },
  });
}
